import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Radio,
  RadioGroup,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
} from '@chakra-ui/react';
import { DragEndEvent } from '@dnd-kit/core';
import { FC } from 'react';
import { MenuCustomer } from 'types/menuCustomer';
import { useMenuDetailStore } from '../../../hooks';
import { CrawlerList } from '../../CrawlerList';
import { MenuDescriptionTextarea, MenuNameInput } from '../../Forms';

type UseMenuDetailStoreReturnType = ReturnType<typeof useMenuDetailStore>;

type Props = UseMenuDetailStoreReturnType['form'] &
  Pick<
    UseMenuDetailStoreReturnType,
    'values' | 'canDelete' | 'crawlers' | 'menuCrawlers'
  > & {
    handleConfirm: () => void;
    handleDeleteConfirm: () => void;
    handleAddCrawler: (id: number, parentId: number) => void;
    handleDeleteCrawler: (id: number, parentId: number) => void;
    handleSortCrawler: (event: DragEndEvent) => void;
    menuCustomers: MenuCustomer[];
  };

export const MenuEditInputModalView: FC<Props> = ({
  register,
  formState,
  values,
  canDelete,
  crawlers,
  menuCrawlers,
  handleConfirm,
  handleDeleteConfirm,
  handleAddCrawler,
  handleDeleteCrawler,
  handleSortCrawler,
  menuCustomers,
}) => (
  <ModalContent maxHeight="90vh">
    <ModalHeader p="40px">メニューの更新</ModalHeader>
    <ModalCloseButton />
    <ModalBody overflowY="auto" px="40px">
      <Stack spacing="6" direction="column">
        <FormControl isRequired isInvalid={!!formState.errors.id}>
          <FormLabel>メニューID</FormLabel>
          <Input
            type="text"
            value={values.id}
            width="240px"
            variant="flushed"
            isReadOnly
            isTruncated
          />
        </FormControl>
        <FormControl isRequired isInvalid={!!formState.errors.name}>
          <FormLabel>メニュー名</FormLabel>
          <MenuNameInput name="name" register={register} />
          <FormErrorMessage>{formState.errors.name?.message}</FormErrorMessage>
        </FormControl>
        <FormControl isRequired>
          <FormLabel>公開範囲</FormLabel>
          <RadioGroup name="type" defaultValue="general">
            <Radio value="general">全顧客</Radio>
          </RadioGroup>
        </FormControl>
        <FormControl isInvalid={!!formState.errors.targetCrawlers}>
          <FormLabel>クローラー</FormLabel>
          <Box
            p="16px"
            borderRadius="md"
            border="solid 1px"
            borderColor="gray.200"
          >
            <CrawlerList
              menuCrawlers={menuCrawlers}
              crawlers={crawlers}
              parentId={-1}
              depth={0}
              update={false}
              handleClickAdd={handleAddCrawler}
              handleDelete={handleDeleteCrawler}
              handleDragEnd={handleSortCrawler}
            />
          </Box>
        </FormControl>
        <FormControl isRequired isInvalid={!!formState.errors.description}>
          <FormLabel>説明</FormLabel>
          <MenuDescriptionTextarea
            name="description"
            register={register}
            rows={4}
          />
          <FormErrorMessage>
            {formState.errors.description?.message}
          </FormErrorMessage>
        </FormControl>
        <Stack spacing={2} direction="column">
          <Text>契約している顧客リスト</Text>
          <Box borderWidth="1px" borderRadius="md" p={4} maxWidth="full">
            {menuCustomers.length === 0 ? (
              <Text fontWeight="bold">
                このメニューを契約している顧客は存在しません
              </Text>
            ) : (
              <TableContainer height="300px" overflowY="auto">
                <Table variant="simple">
                  <Thead position="sticky" top={0} bgColor="white" zIndex={1}>
                    <Tr>
                      <Th>ID</Th>
                      <Th>顧客名</Th>
                      <Th>顧客種別</Th>
                      <Th>Google Cloud プロジェクト名</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {menuCustomers.map((e) => (
                      <Tr key={e.customerId}>
                        <Td>{e.customerId}</Td>
                        <Td>{e.customerName}</Td>
                        <Td>{e.channel}</Td>
                        <Td>{e.environment}</Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </Stack>
      </Stack>
    </ModalBody>
    <ModalFooter justifyContent="center" p="40px">
      <Stack alignItems="center" spacing="24px">
        <Button
          w="140px"
          colorScheme="blackAlpha"
          onClick={handleConfirm}
          isDisabled={!formState.isValid}
        >
          確認
        </Button>
        <Tooltip
          maxW="initial"
          label={
            canDelete
              ? ''
              : 'クローラーが1つ以上登録されている為、削除できません'
          }
        >
          <Box>
            <Button
              variant="link"
              colorScheme="blackAlpha"
              onClick={handleDeleteConfirm}
              isDisabled={!canDelete}
            >
              このメニューを削除
            </Button>
          </Box>
        </Tooltip>
      </Stack>
    </ModalFooter>
  </ModalContent>
);
